import { formatDate } from '@seen/core/dates'
import { DOCUMENT_CATEGORIES } from '@seen/core/models/document-references/constants'
import { cn } from '@seen/ui'
import { IconSignatureOff } from '@tabler/icons-react'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { HighlightedText } from '#src/components/core/HighlightedText.tsx'
import { SearchMatchSnippet } from '#src/components/core/SearchMatchSnippet.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { CategoryCircles } from '#src/components/document-picker/CategoryCircles.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { countStemOccurrencesMulti } from '#src/hooks/useClientSideSearch.ts'

interface DocumentPickerItemProps {
  document: DocumentPickerDocument
  isSelected: boolean
  isActive: boolean
  /**
   * Stemmed query terms from `matchById.get(id)?.terms` — present only when a
   * search query is active. When provided, title and subtitle are highlighted
   * and a snippet is shown from the best-matching hidden field.
   */
  matchTerms?: string[]
  /**
   * Pre-extracted field text from `getExtractedFields(id)` — present only
   * when a search query is active. Used to source snippet text.
   */
  extractedFields?: Record<string, string>
  onSelectionChange?: (selected: boolean, shiftKey: boolean) => void
  onActiveChange?: () => void
  onDoubleClick?: () => void
  className?: string
}

export function DocumentPickerItem({
  document,
  isSelected,
  isActive,
  matchTerms,
  extractedFields,
  onSelectionChange,
  onActiveChange,
  onDoubleClick,
  className,
}: DocumentPickerItemProps) {
  const categories = document.categories ?? []
  const categoryNames = categories
    .map((cat) => DOCUMENT_CATEGORIES[cat]?.display ?? cat)
    .join(', ')

  /* Determine which hidden field (if any) has the most match hits — this
   * is the field we'll show a snippet from. "Hidden" means not already
   * displayed as title or subtitle; the snippet only shows when a search
   * is active and a non-title/subtitle field drove the match.
   *
   * To prevent duplication: the field that currently sources the subtitle
   * is excluded from snippet candidates. The subtitle source is the
   * HTML-stripped description when it is non-empty; otherwise it is
   * aiSummary (matching the `description || aiSummary` fallback upstream). */
  const subtitleSourceField: string =
    (extractedFields?.description ?? '').length > 0
      ? 'description'
      : 'aiSummary'

  const snippetField = (() => {
    if (!matchTerms || matchTerms.length === 0 || !extractedFields) {
      return null
    }
    const hiddenFields: Record<string, string> = {}
    for (const key of ['description', 'aiSummary', 'transcription']) {
      /* Exclude the field shown as subtitle — snippet would duplicate it. */
      if (key === subtitleSourceField) {
        continue
      }
      const text = extractedFields[key]
      if (text) {
        hiddenFields[key] = text
      }
    }
    if (Object.keys(hiddenFields).length === 0) {
      return null
    }
    const { perField } = countStemOccurrencesMulti(hiddenFields, matchTerms)
    let bestField: string | null = null
    let bestCount = 0
    for (const [field, count] of Object.entries(perField)) {
      if (count > bestCount) {
        bestCount = count
        bestField = field
      }
    }
    if (!bestField || bestCount === 0) {
      return null
    }
    return {
      field: bestField,
      text: hiddenFields[bestField],
      matchCount: bestCount,
    }
  })()

  const hasSnippet = snippetField !== null

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- DocumentPickerList handles keyboard navigation and selection for its rows
    <div
      className={cn(
        'flex cursor-pointer items-start gap-3 border-b px-3 py-1',
        hasSnippet ? 'min-h-14' : 'h-14',
        isActive ? 'bg-accent' : 'hover:bg-muted/50',
        className
      )}
      onClick={onActiveChange}
      onDoubleClick={onDoubleClick}
    >
      <div className="flex shrink-0 items-center pt-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center">
              <Checkbox
                value={isSelected}
                onClick={(e) => {
                  e.stopPropagation()
                  onSelectionChange?.(!isSelected, e.shiftKey)
                  onActiveChange?.()
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="flex items-center gap-2">
            Select document
            <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">
              X
            </kbd>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1">
        <div className="flex min-w-0 items-center gap-1">
          <HighlightedText
            text={document.title}
            terms={matchTerms}
            className="truncate text-sm font-medium"
          />
          {document.isSigned === false && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconSignatureOff className="ml-auto size-3.5 shrink-0 text-foreground-tertiary" />
              </TooltipTrigger>
              <TooltipContent side="right">Not signed</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {categories.length > 0 ? (
              <>
                <CategoryCircles categories={categories} className="shrink-0" />
                <span className="truncate text-2xs text-muted-foreground">
                  {categoryNames}
                </span>
              </>
            ) : (
              <HighlightedText
                text={document.subtitle || 'No description'}
                terms={matchTerms}
                className="truncate text-2xs text-muted-foreground"
              />
            )}
          </div>
          {document.date ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatDate(document.date)}
            </span>
          ) : null}
        </div>
        {snippetField && (
          <SearchMatchSnippet
            hiddenFieldText={snippetField.text}
            stemmedTerms={matchTerms ?? []}
            hiddenFieldMatches={snippetField.matchCount}
          />
        )}
      </div>
    </div>
  )
}
